import { createRouter, createWebHistory } from 'vue-router'
import type { RouteLocationNormalizedLoaded, RouteRecordRaw } from 'vue-router'

import MainLayout from '@/layouts/MainLayout.vue'
import { buildDocumentTitle } from '@/router/title'
import { isPublicRoute } from '@/router/access'
import { useAuthStore } from '@/features/account/session'
import { useCharactersStore } from '@/stores/characters'
import { useConversationsStore } from '@/stores/conversations'
import { useRoleplayConversationSessionStore } from '@/features/roleplay-conversation'
import ConversationView from '@/views/ConversationView.vue'
import HomeView from '@/views/HomeView.vue'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: MainLayout,
    children: [
      {
        path: 'admin/prompt-captures',
        name: 'admin-prompt-captures',
        component: () => import('@/views/AdminPromptCapturesView.vue'),
        meta: { background: 'auth-gradient', title: 'Prompt Captures', chrome: 'header',
          ascent: { to: '/admin/access' }, hideTabBar: true },
      },
      {
        path: '',
        name: 'home',
        component: HomeView,
        meta: {
          background: 'ambient-gradient',
          // Home is the root floor: no ascent, but its chrome declares the broad
          // section launcher via the same `links` mechanism every route uses.
          // Identity (wordmark) sits left; account controls (avatar + settings)
          // are teleported into the tools region on the right by HomeView.
          title: 'Storyteller',
          chrome: 'header+tools',
          links: [
            { to: '/characters', label: 'Characters', iconName: 'characters' },
            { to: '/conversations', label: 'Conversations', iconName: 'conversations' },
            { to: '/lorebooks', label: 'Lorebooks', iconName: 'lorebooks' },
            { to: '/worlds', label: 'Worlds', iconName: 'worlds' },
            { to: '/personas', label: 'Personas', iconName: 'personas' },
            { to: '/creation', label: 'Creator', iconName: 'creator' },
            { to: '/open-worlds', label: 'Open Worlds', iconName: 'worlds' },
          ],
        },
      },
      {
        path: 'conversation/:id',
        name: 'conversation',
        component: ConversationView,
        props: true,
        meta: {
          background: 'story-gradient',
          hideTabBar: true,
          chrome: 'header+tools',
          chromeRecessive: true,
          showAscentOnMobileRecessive: true,
          hideHeaderOnMobile: false,
        },
      },
      {
        path: 'open-worlds',
        name: 'open-worlds',
        component: () => import('@/features/open-worlds/OpenWorldsView.vue'),
        meta: { background: 'story-gradient', title: 'Open Worlds', chrome: 'header', ascent: { to: '/' } },
      },
      {
        path: 'open-worlds/:id/edit',
        name: 'open-world-editor',
        component: () => import('@/features/open-worlds/OpenWorldEditorView.vue'),
        meta: { background: 'none', title: 'Setup', hideTabBar: true,
          chrome: 'header+tools', ascent: { to: '/open-worlds' } },
      },
      {
        path: 'open-worlds/play/:id',
        name: 'open-world-play',
        component: () => import('@/features/open-worlds/OpenWorldPlayView.vue'),
        meta: { background: 'story-gradient', title: 'Open World', hideTabBar: true,
          chrome: 'header+tools', chromeRecessive: true, showAscentOnMobileRecessive: true,
          ascent: { to: '/open-worlds' } },
      },
      {
        path: 'characters',
        name: 'characters',
        component: () => import('@/views/CharactersView.vue'),
        meta: { background: 'library-gradient', title: 'Characters', chrome: 'header+tools', ascent: { to: '/' } },
      },
      {
        path: 'lorebooks',
        name: 'lorebooks',
        component: () => import('@/views/LorebooksView.vue'),
        meta: { background: 'library-gradient', title: 'Lorebooks', chrome: 'header+tools', ascent: { to: '/' } },
      },
      {
        path: 'worlds',
        name: 'worlds',
        redirect: { path: '/creation', query: { section: 'worlds' } },
      },
      {
        path: 'creation',
        name: 'creator-home',
        component: () => import('@/views/creation/CreatorEntryView.vue'),
        meta: { background: 'none', title: 'AI Creator', hideTabBar: true, chrome: 'header+tools', ascent: { to: '/' } },
      },
      {
        path: 'creation/:id',
        name: 'creator-session',
        component: () => import('@/views/creation/CreatorWorkspaceHostView.vue'),
        props: true,
        meta: {
          background: 'none',
          title: 'AI Creator',
          hideTabBar: true,
          chrome: 'header+tools',
          chromeRecessive: true,
          showAscentOnMobileRecessive: true,
          ascent: { to: '/creation' },
        },
      },
      {
        path: 'create-lorebook',
        name: 'create-lorebook',
        redirect: '/creation',
      },
      {
        path: 'user/:id',
        name: 'user',
        component: () => import('@/views/UserView.vue'),
        meta: { background: 'profile-gradient', title: 'User', chrome: 'header', ascent: { to: '/' } },
      },
      {
        path: 'user/:id/edit',
        name: 'edit-profile',
        component: () => import('@/views/ProfileEditorView.vue'),
        props: true,
        meta: {
          background: 'profile-gradient',
          title: 'Edit Profile',
          hideTabBar: true,
          chrome: 'header+tools',
          ascent: { name: 'user', paramKeys: ['id'] },
        },
      },
      {
        path: 'world/:id',
        name: 'world',
        component: () => import('@/views/WorldView.vue'),
        meta: { background: 'story-gradient', title: 'World', showLoadingOverlay: false, chrome: 'header', ascent: { to: '/worlds' } },
      },
      {
        path: 'world/:id/editor',
        name: 'world-editor',
        component: () => import('@/views/creation/WorldEditorRedirectView.vue'),
        meta: {
          background: 'editor-gradient',
          title: 'World Editor',
          hideTabBar: true,
          chrome: 'header+tools',
          chromeRecessive: true,
          ascent: { to: '/creation' },
        },
      },
      {
        path: 'character/:id',
        name: 'character',
        component: () => import('@/views/CharacterView.vue'),
        props: true,
        meta: { background: 'story-gradient', title: 'Character', chrome: 'header', ascent: { to: '/characters' } },
      },
      {
        path: 'create-character',
        name: 'create-character',
        redirect: '/creation',
      },
      {
        path: 'creation/character',
        name: 'character-creator',
        redirect: '/creation',
      },
      {
        path: 'conversations',
        name: 'conversations',
        component: () => import('@/views/ConversationsView.vue'),
        meta: { background: 'library-gradient', title: 'Conversations', chrome: 'header', ascent: { to: '/' } },
      },
      {
        path: 'personas',
        name: 'personas',
        component: () => import('@/features/personas/PersonasView.vue'),
        meta: { background: 'profile-gradient', title: 'Personas', chrome: 'header', ascent: { to: '/' } },
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/views/SettingsView.vue'),
        meta: { background: 'editor-gradient', title: 'Settings', chrome: 'header', ascent: { to: '/' } },
      },
      {
        path: 'test-navigation',
        name: 'test-navigation',
        component: () => import('@/views/testing/TestNavigationView.vue'),
        meta: { testing: true, title: 'Test Navigation' },
      },
      {
        path: 'testing',
        name: 'testing',
        component: () => import('@/views/testing/TestingIndexView.vue'),
        meta: { testing: true, title: 'Testing' },
      },
      {
        path: 'info-tooltip-test',
        name: 'info-tooltip-test',
        component: () => import('@/views/testing/InfoTooltipTestView.vue'),
        meta: { testing: true, title: 'Info Tooltip Test' },
      },
      {
        path: 'layout-primitives-playground',
        name: 'layout-primitives-playground',
        component: () => import('@/views/testing/LayoutPrimitivesPlaygroundView.vue'),
        meta: { testing: true, title: 'Layout Playground' },
      },
      {
        path: 'base-container-test',
        name: 'base-container-test',
        component: () => import('@/views/testing/BaseContainerTestView.vue'),
        meta: { testing: true, title: 'Base Container Test' },
      },
      {
        path: 'notification-banner-test',
        name: 'notification-banner-test',
        component: () => import('@/views/testing/NotificationBannerTestView.vue'),
        meta: { testing: true, title: 'Notification Banner Test' },
      },
      {
        path: 'core-form-system-test',
        name: 'core-form-system-test',
        component: () => import('@/views/testing/CoreFormSystemTestView.vue'),
        meta: { testing: true, title: 'Core Form System Test' },
      },
      {
        path: 'svg-testing',
        name: 'svg-testing',
        component: () => import('@/views/testing/SvgTestingView.vue'),
        meta: { testing: true, title: 'SVG Testing' },
      },
      {
        path: 'composer-test',
        name: 'composer-test',
        component: () => import('@/views/testing/ComposerTestView.vue'),
        meta: { testing: true, title: 'Composer Test' },
      },
      {
        path: 'av-runes',
        name: 'av-runes',
        component: () => import('@/views/testing/AvRuneTestView.vue'),
        meta: { testing: true, title: 'Avi Runes' },
      },
    ],
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { background: 'auth-gradient', showLoadingOverlay: false, title: 'Log In' },
  },
  {
    path: '/request-access',
    name: 'request-access',
    component: () => import('@/views/RequestAccessView.vue'),
    meta: { background: 'auth-gradient', showLoadingOverlay: false, title: 'Request Access' },
  },
  {
    path: '/auth/callback',
    name: 'auth-callback',
    component: () => import('@/views/AuthCallbackView.vue'),
    meta: { background: 'auth-gradient', showLoadingOverlay: false, title: 'Signing In' },
  },
  {
    path: '/admin/access',
    name: 'admin-access',
    component: () => import('@/views/AdminAccessView.vue'),
    meta: { background: 'auth-gradient', showLoadingOverlay: false, title: 'Admin Access' },
  },
  {
    path: '/reset-password',
    name: 'password-reset',
    component: () => import('@/views/PasswordResetView.vue'),
    meta: { background: 'auth-gradient', showLoadingOverlay: false, title: 'Reset Password' },
  },
]

const liveTitleRouteNames = new Set(['conversation', 'character'])

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

let activeTitleUnsubscribers: Array<() => void> = []

const stopTitleSync = (): void => {
  activeTitleUnsubscribers.forEach((unsubscribe) => {
    try {
      unsubscribe()
    } catch {
      // ignore unsubscribe failures during route churn
    }
  })
  activeTitleUnsubscribers = []
}

const setDocumentTitle = (to: RouteLocationNormalizedLoaded): void => {
  const routeName = to.name
  const metaTitle = to.meta.title

  if (routeName === 'conversation') {
    const conversationsStore = useConversationsStore()
    const sessionStore = useRoleplayConversationSessionStore()
    const charactersStore = useCharactersStore()
    const conversationId = Number(to.params.id)

    const conversation =
      sessionStore.resources.conversation?.id === conversationId
        ? sessionStore.resources.conversation
        : conversationsStore.getConversationById(conversationId)

    const character =
      sessionStore.resources.character
      || (conversation &&
        (charactersStore.getCharacterById(conversation.character_id) ||
          charactersStore.currentCharacter))

    document.title = buildDocumentTitle({
      routeName,
      metaTitle,
      conversationTitle: conversation?.title,
      characterName: character?.character_name,
    })
    return
  }

  if (routeName === 'character') {
    const charactersStore = useCharactersStore()
    const characterId = Number(to.params.id)
    const character =
      charactersStore.getCharacterById(characterId) || charactersStore.currentCharacter

    document.title = buildDocumentTitle({
      routeName,
      metaTitle,
      characterName: character?.character_name,
    })
    return
  }

  document.title = buildDocumentTitle({ routeName, metaTitle })
}

router.afterEach((to) => {
  stopTitleSync()
  setDocumentTitle(to)

  if (!liveTitleRouteNames.has(String(to.name ?? ''))) return

  const charactersStore = useCharactersStore()
  const expectedId = String(to.params.id ?? '')

  const updateIfStillActive = (): void => {
    const current = router.currentRoute.value
    if (!liveTitleRouteNames.has(String(current.name ?? ''))) return
    if (String(current.params.id ?? '') !== expectedId) return
    setDocumentTitle(current)
  }

  if (to.name === 'conversation') {
    const sessionStore = useRoleplayConversationSessionStore()
    activeTitleUnsubscribers.push(
      sessionStore.$subscribe(updateIfStillActive, { detached: true }),
    )
  }

  activeTitleUnsubscribers.push(
    charactersStore.$subscribe(updateIfStillActive, { detached: true }),
  )
})

// Global router guard.
router.beforeEach(async (to) => {
  const authStore = useAuthStore()

  if (isPublicRoute(to.name)) {
    return true
  }

  await authStore.initialize()
  // Unknown session state is recoverable in App; it is not a confirmed sign-out.
  if (authStore.status === 'error') return true

  if (!authStore.isAuthenticated) {
    return {
      name: 'login',
      query: { redirect: to.fullPath },
    }
  }

  return true
})

export default router
